/**
 * Request Deduplicator
 * Evita requisições duplicadas para o mesmo endpoint
 * Requisições idênticas em andamento são reutilizadas
 */

const pendingRequests = new Map();
const requestCache = new Map();

// Cache TTL em ms (5 segundos para dados dinâmicos)
const DEFAULT_CACHE_TTL = 5000;

/**
 * Gera uma chave única para a requisição
 */
function generateRequestKey(config) {
  const { method = 'get', url, params, data } = config;
  const paramsString = params ? JSON.stringify(params) : '';
  const dataString = data ? JSON.stringify(data) : '';
  return `${method.toUpperCase()}:${url}:${paramsString}:${dataString}`;
}

/**
 * Verifica se há cache válido para a requisição
 */
function getCachedResponse(key, ttl = DEFAULT_CACHE_TTL) {
  const cached = requestCache.get(key);
  if (!cached) return null;

  const { timestamp, response } = cached;
  const isExpired = Date.now() - timestamp > ttl;

  if (isExpired) {
    requestCache.delete(key);
    return null;
  }

  return response;
}

/**
 * Armazena resposta no cache
 */
function setCachedResponse(key, response) {
  requestCache.set(key, {
    timestamp: Date.now(),
    response,
  });
}

/**
 * Limpa cache expirado periodicamente
 */
function cleanupCache() {
  const now = Date.now();
  const keysToDelete = [];

  requestCache.forEach((value, key) => {
    if (now - value.timestamp > DEFAULT_CACHE_TTL * 2) {
      keysToDelete.push(key);
    }
  });

  keysToDelete.forEach(key => requestCache.delete(key));
}

// Limpa cache a cada 30 segundos
setInterval(cleanupCache, 30000);

/**
 * Interceptor de request para axios
 * Adiciona deduplicação de requisições
 */
export function setupRequestDeduplication(axiosInstance) {
  // Request interceptor
  axiosInstance.interceptors.request.use(
    config => {
      // Só deduplica GET requests
      if (config.method?.toLowerCase() !== 'get') {
        return config;
      }

      // Permite desabilitar deduplicação por request
      if (config.skipDeduplication) {
        return config;
      }

      const key = generateRequestKey(config);

      // Verifica cache primeiro
      const cached = getCachedResponse(key, config.cacheTTL);
      if (cached) {
        // Cria uma promise que resolve imediatamente com cache
        const controller = new AbortController();
        config.signal = controller.signal;
        // eslint-disable-next-line no-underscore-dangle
        config.__fromCache = true;
        // eslint-disable-next-line no-underscore-dangle
        config.__cachedResponse = cached;

        // Cancela request real e retorna cache
        setTimeout(() => controller.abort('FROM_CACHE'), 0);
      }

      // Verifica se já tem request pendente
      if (pendingRequests.has(key)) {
        const existingPromise = pendingRequests.get(key);
        // eslint-disable-next-line no-underscore-dangle
        config.__deduplicated = true;
        // eslint-disable-next-line no-underscore-dangle
        config.__existingPromise = existingPromise;
      } else {
        // Marca como pendente
        // eslint-disable-next-line no-underscore-dangle
        config.__requestKey = key;
      }

      return config;
    },
    error => Promise.reject(error)
  );

  // Response interceptor
  axiosInstance.interceptors.response.use(
    response => {
      // eslint-disable-next-line no-underscore-dangle
      const key = response.config.__requestKey;

      if (key) {
        // Remove da lista de pendentes
        pendingRequests.delete(key);

        // Armazena no cache (só GET)
        if (response.config.method?.toLowerCase() === 'get') {
          setCachedResponse(key, response);
        }
      }

      return response;
    },
    error => {
      // Se foi cancelado para usar cache
      // eslint-disable-next-line no-underscore-dangle
      if (error.message === 'FROM_CACHE' && error.config?.__cachedResponse) {
        // eslint-disable-next-line no-underscore-dangle
        return Promise.resolve(error.config.__cachedResponse);
      }

      // eslint-disable-next-line no-underscore-dangle
      const key = error.config?.__requestKey;
      if (key) {
        pendingRequests.delete(key);
      }

      return Promise.reject(error);
    }
  );
}

/**
 * Wrapper para deduplicate manual de funções de API
 */
export function deduplicateRequest(fn, keyGenerator) {
  return async (...args) => {
    const key = keyGenerator ? keyGenerator(...args) : JSON.stringify(args);

    // Verifica cache
    const cached = getCachedResponse(key);
    if (cached) {
      return cached.data;
    }

    // Verifica se já tem request pendente
    if (pendingRequests.has(key)) {
      return pendingRequests.get(key);
    }

    // Cria nova promise
    const promise = fn(...args)
      .then(response => {
        setCachedResponse(key, response);
        return response.data;
      })
      .finally(() => {
        pendingRequests.delete(key);
      });

    pendingRequests.set(key, promise);
    return promise;
  };
}

/**
 * Limpa todo o cache (útil para logout ou refresh)
 */
export function clearRequestCache() {
  requestCache.clear();
  pendingRequests.clear();
}

export default {
  setupRequestDeduplication,
  deduplicateRequest,
  clearRequestCache,
};
